<script setup>
import { ref, watch, reactive, computed, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import api from '@/services/api'
import DocumentoEntradaAcoesGrid from '@/components/base/grid/DocumentoEntradaAcoesGrid.vue'
import DocumentoEntradaModal from '@/components/Compras/documentoEntrada/DocumentoEntradaModal.vue'
import { SUP_ST_DOC_ENT } from '@/constants/SUP_ST_DOC_ENT'
import { OC_STATUS } from '@/constants/OC_STATUS'
import { DOC_ENT_LOCAL } from '@/constants/DOC_ENT_LOCAL'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { useDocumentoEntradaAcoes } from '@/composables/useDocumentoEntradaAcoes'

const authStore = useAuthStore()

const props = defineProps({
  idPurchaseOrder: { type: Number, default: null },
  idEmbarque: { type: Number, default: null },
  idEmbarqueContainer: { type: Number, default: null },
  idOrdemCompra: { type: Number, default: null },
  selecionavel: { type: Boolean, default: true },
  documentoSelecionado: { type: Object, default: null },
  temModuloDocumento: { type: Boolean, default: false },
  temCompleto: { type: Boolean, default: false },
  temVisualizacao: { type: Boolean, default: false },
  podeEditar: { type: Boolean, default: false },
  listas: { type: Object, default: () => ({}) },
  exibirPrimeiroProduto: { type: Boolean, default: false },
  // === PROPS REPASSADAS PARA O DocumentoEntradaModal ===
  idFilial: { type: Number, default: null },
  lstMoeda: { type: String, default: '' },
  idOperacao: { type: Number, default: null },
  idParceiroNegocioEmissor: { type: Number, default: null },
  isInterno: { type: Boolean, default: false },
  idOrganizacao: { type: Number, default: null },
  lstTipoTaxaConversao: { type: String, default: null },
  comSmartMode: { type: Boolean, default: false },
  habilitarParceiroProdutor: { type: Boolean, default: false },
  configBuscaLoteFabricacao: { type: Object, default: () => ({}) },
  idParceiroNegocioFornecedor: { type: Number, default: null },
  contextoOrigem: { type: String, default: 'outros' },
  // === MODO DE ALOCAÇÃO DO SMART MODE ===
  /// <summary>'saldo' = alocação via lista de saldos; 'livre' = via cadastro de produtos.
  /// Definido explicitamente pelo componente pai.</summary>
  modoAlocacao: { type: String, default: 'livre' },
  // === LOCAL DE ESTOQUE (seleção na etapa Identificação, conforme operação) ===
  lstTiposLocalEstoque: { type: Array, default: () => [] },
  // === VALOR PADRÃO PARA LOCAL DE ESTOQUE (propagado do cabeçalho da OC) ===
  idLocalEstoquePadrao: { type: Number, default: null },
  // === CONDIÇÃO E FORMA DE PAGAMENTO (propagados do cabeçalho da OC) ===
  idCondicaoPagamento: { type: Number, default: null },
  lstFormaPagamento: { type: String, default: null },
  lstFormaPagamentoInternacional: { type: String, default: null },
  // === STATUS DA ORDEM DE COMPRA (validação para vincular documentos) ===
  lstStatusOrdemCompra: { type: String, default: null },
  // === ACCORDION (colapso do card) ===
  /// <summary>Quando true, habilita o comportamento de accordion (header clicável para colapsar/expandir).</summary>
  comAccordion: { type: Boolean, default: false },
  /// <summary>Estado do accordion (aberto/fechado). Usar com v-model para controle bidirecional.</summary>
  accordionAberto: { type: Boolean, default: true }
})

const emit = defineEmits([
  'update:documentoSelecionado',
  'update:accordionAberto',
  'editar',
  'visualizar',
  'acao-completa',
  'show-notification',
  'novo-documento'
])

// ============================================================
// ESTADO
// ============================================================
const documentosEntrada = ref([])
const isLoading = ref(false)

const metadataStore = useMetadataListasStore()
const listasInternas = reactive({})

// ============================================================
// ESTADO DO MODAL INTERNO
// ============================================================
const showModalDocumento = ref(false)
const modalDocumentoModo = ref('criacao') // 'criacao' | 'edicao' | 'visualizacao'
const modalDocumentoData = ref(null)

const carregarListasInternas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['DocumentoEntrada'])
    Object.assign(listasInternas, result)
  } catch (e) {
    console.error('[DocumentoEntradaGrid] erro ao carregar listas internas:', e)
  }
}

onMounted(() => {
  carregarListasInternas()
})

// ============================================================
// CARREGAR DOCUMENTOS
// ============================================================
const carregarDocumentos = async () => {
  const params = { pageSize: 999 }

  if (props.idPurchaseOrder) {
    params.idPurchaseOrder = props.idPurchaseOrder
  } else if (props.idEmbarque) {
    params.idEmbarque = props.idEmbarque
  } else if (props.idEmbarqueContainer) {
    params.idEmbarqueContainer = props.idEmbarqueContainer
  } else if (props.idOrdemCompra) {
    params.idOrdemCompra = props.idOrdemCompra
  } else {
    documentosEntrada.value = []
    return
  }

  isLoading.value = true
  try {
    const res = await api.get('/DocumentosEntrada', { params })
    documentosEntrada.value = res.data
  } catch (e) {
    emit('show-notification', { message: 'Erro ao carregar documentos de entrada.', type: 'error' })
  } finally {
    isLoading.value = false
  }
}

// ============================================================
// WATCH — recarrega quando qualquer ID mudar
// ============================================================
watch(
  () => [props.idPurchaseOrder, props.idEmbarque, props.idEmbarqueContainer, props.idOrdemCompra],
  () => {
    carregarDocumentos()
  },
  { immediate: true }
)

// ============================================================
// WATCH — recarrega a lista quando o modal for fechado
// (cobre o caso de adicionar/remover itens via SmartMode
// dentro do modal, que não passa pelo @salvar do grid)
// ============================================================
watch(showModalDocumento, (novoValor, valorAntigo) => {
  if (!novoValor && valorAntigo) {
    carregarDocumentos()
    // Notifica o componente pai (PurchaseOrderForm) que o modal foi fechado
    // para que ele possa recarregar os totais consumidos da PO
    console.log('[DEBUG DocumentoEntradaGrid] modal fechado -> emit acao-completa')
    emit('acao-completa', { tipoAcao: 'fechar-modal', documento: null })
  }
})

// ============================================================
// HELPERS
// ============================================================
const getTextoLista = (lista, id) => {
  if (!lista) return id || '-'
  const found = lista.find(l => String(l.idListaClienteItem) === String(id))
  return found ? found.textoLista : (id || '-')
}

const getStatusTexto = (id) => {
  const lista = listasInternas.LstStatusDocumento || props.listas?.LstStatusDocumento || []
  const texto = getTextoLista(lista, id)
  return texto || id || '-'
}

const obterCorStatus = (textoStatus) => {
  const s = String(textoStatus || '').toUpperCase()
  if (s.includes('CANCELADO') || s.includes('INATIVO') || s.includes('REPROVADO')) return 'badge-ghost-danger'
  if (s.includes('ABERTO') || s.includes('PENDENTE') || s.includes('EM DIGITAÇÃO') || s.includes('ST1')) return 'badge-ghost-warning'
  if (s.includes('ATIVO') || s.includes('APROVADO') || s.includes('CONCLUÍDO') || s.includes('FINALIZADO')) return 'badge-ghost-success'
  if (s.includes('EM ANÁLISE') || s.includes('ENVIADO')) return 'badge-ghost-info'
  if (s.includes('PROCESSANDO')) return 'badge-ghost-primary'
  return 'badge-ghost-secondary'
}

const formatarDecimal = (valor) => {
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor || 0)
}

// ============================================================
// VALIDAÇÃO DO STATUS DA ORDEM DE COMPRA
// Regra de negócio: apenas Emitida e Faturada Parcialmente
// permitem gerar/editar Documentos de Entrada.
// ============================================================
const STATUS_OC_PERMITIDOS = [
  OC_STATUS.Emitida,
  OC_STATUS.FaturadaParcialmente
]

const statusOcPermiteDocumento = computed(() => {
  if (!props.lstStatusOrdemCompra) return true
  return STATUS_OC_PERMITIDOS.includes(props.lstStatusOrdemCompra)
})

const statusOcNome = computed(() => {
  if (!props.lstStatusOrdemCompra) return ''
  const nomes = {
    [OC_STATUS.Rascunho]: 'Rascunho',
    [OC_STATUS.EmAprovacao]: 'Em Aprovação',
    [OC_STATUS.Aprovada]: 'Aprovada',
    [OC_STATUS.Emitida]: 'Emitida',
    [OC_STATUS.FaturadaParcialmente]: 'Faturada Parcialmente',
    [OC_STATUS.EncerradaParcialmente]: 'Encerrada Parcialmente',
    [OC_STATUS.Encerrada]: 'Encerrada',
    [OC_STATUS.Reprovada]: 'Reprovada',
    [OC_STATUS.Cancelada]: 'Cancelada'
  }
  return nomes[props.lstStatusOrdemCompra] || props.lstStatusOrdemCompra
})

const mensagemStatusOcBloqueio = computed(() => {
  if (!props.lstStatusOrdemCompra || statusOcPermiteDocumento.value) return ''
  return `O status da Ordem de Compra '${statusOcNome.value}' não permite vincular Documentos de Entrada. São permitidos apenas os status: Emitida ou Faturada Parcialmente.`
})

// ============================================================
// SELECIONAR DOCUMENTO
// ============================================================
const selecionarDocumento = (doc) => {
  if (!props.selecionavel) return
  if (doc.lstStatusDocumento === SUP_ST_DOC_ENT.Cancelado) return
  emit('update:documentoSelecionado', doc)
}

// ============================================================
// HANDLERS DO MODAL (agora gerenciados internamente)
// ============================================================
const abrirModalDocumento = (documento = null) => {
  modalDocumentoModo.value = documento ? 'edicao' : 'criacao'
  modalDocumentoData.value = documento
  showModalDocumento.value = true
}

const abrirModalVisualizarDocumento = (documento) => {
  modalDocumentoModo.value = 'visualizacao'
  modalDocumentoData.value = documento
  showModalDocumento.value = true
}

const handleAcaoCompleta = ({ tipoAcao, documento }) => {
  if (tipoAcao === 'excluir' && props.documentoSelecionado?.idDocumentoEntrada === documento.idDocumentoEntrada) {
    emit('update:documentoSelecionado', null)
  }
  emit('acao-completa', { tipoAcao, documento })
  carregarDocumentos()
}

const handleSalvarDocumentoModal = async (payload) => {
  // Se estiver em fluxo de aprovação, usa o callback interno
  if (approvalModalState.value) {
    await handleSalvarDocumentoAprovacao(payload)
    showModalDocumento.value = false
    await carregarDocumentos()
    return
  }

  // Salva padrão: chama API diretamente
  try {
    if (payload.idDocumentoEntrada) {
      await api.put(`/DocumentosEntrada/${payload.idDocumentoEntrada}`, payload)
    } else {
      await api.post('/DocumentosEntrada', payload)
    }
    emit('show-notification', { message: 'Documento de entrada salvo com sucesso!', type: 'success' })
    showModalDocumento.value = false
    await carregarDocumentos()
  } catch (error) {
    const errorMsg = error.response?.data?.message || 'Erro ao salvar documento de entrada.'
    emit('show-notification', { message: errorMsg, type: 'error' })
  }
}

const handleSalvarDocumentoIntermediario = async (payload) => {
  try {
    let response
    if (payload.idDocumentoEntrada) {
      response = await api.put(`/DocumentosEntrada/${payload.idDocumentoEntrada}`, payload)
    } else {
      response = await api.post('/DocumentosEntrada', payload)
    }
    const documentoSalvo = response.data || payload
    modalDocumentoData.value = documentoSalvo
    modalDocumentoModo.value = 'edicao'
    emit('show-notification', { message: 'Documento de entrada salvo! Agora você pode gerenciar os itens.', type: 'success' })
    // Notifica o componente pai para recarregar os totais consumidos imediatamente
    emit('acao-completa', { tipoAcao: 'salvar-intermediario', documento: documentoSalvo })
  } catch (error) {
    const errorMsg = error.response?.data?.message || 'Erro ao salvar documento de entrada.'
    emit('show-notification', { message: errorMsg, type: 'error' })
  }
}

// ============================================================
// USEDOCUMENTOENTRADAACOES (fluxo de aprovação interno)
// ============================================================
const {
  iniciarAprovacaoComVerificacao,
  executarAprovacaoViaModal,
  cancelarAprovacaoViaModal,
  approvalModalState
} = useDocumentoEntradaAcoes({
  showNotification: (p) => emit('show-notification', p),
  onSuccess: (tipoAcao, documento) => {
    approvalModalState.value = null
    emit('acao-completa', { tipoAcao, documento })
    carregarDocumentos()
  }
})

// Callback para o fluxo de aprovação: o modal chama este callback via onSalvarDocumento
const handleSalvarDocumentoAprovacao = async (payload) => {
  if (approvalModalState.value) {
    await executarAprovacaoViaModal(payload)
  }
}

// Callback para cancelar aprovação
const handleCancelarAprovacao = () => {
  cancelarAprovacaoViaModal()
}

// Watch para abrir o modal interno quando o approvalModalState mudar
watch(approvalModalState, (state) => {
  if (state) {
    abrirModalDocumento(state.documento)
  }
})

// ============================================================
// HANDLERS DE AÇÃO DO GRID
// ============================================================
const handleEditar = (doc) => {
  if (!statusOcPermiteDocumento.value) {
    emit('show-notification', { message: mensagemStatusOcBloqueio.value, type: 'warning' })
    return
  }
  abrirModalDocumento(doc)
}

const handleVisualizar = (doc) => {
  abrirModalVisualizarDocumento(doc)
}

const handleAprovar = (doc) => {
  iniciarAprovacaoComVerificacao(doc)
}

const handleNovoDocumento = () => {
  if (!statusOcPermiteDocumento.value) {
    emit('show-notification', { message: mensagemStatusOcBloqueio.value, type: 'warning' })
    return
  }
  abrirModalDocumento()
}

// Computed para approvalConfig reativo baseado no estado do modal de aprovação
const approvalConfigReativo = computed(() => {
  if (!approvalModalState.value) return null
  return { camposPendentes: approvalModalState.value.camposPendentes }
})

// ============================================================
// COMPUTED: LstLocalDocumento (origem do documento)
// ============================================================
const lstLocalDocumento = computed(() => {
  if (props.idPurchaseOrder) return DOC_ENT_LOCAL.PurchaseOrder
  if (props.idOrdemCompra) return DOC_ENT_LOCAL.OrdemCompra
  if (props.idEmbarque) return DOC_ENT_LOCAL.Embarque
  if (props.idEmbarqueContainer) return DOC_ENT_LOCAL.Container
  return null
})

// ============================================================
// ACCORDION (colapso do card)
// ============================================================
const accordionInterno = ref(props.accordionAberto)

watch(() => props.accordionAberto, (val) => {
  accordionInterno.value = val
})

const toggleAccordion = () => {
  if (!props.comAccordion) return
  accordionInterno.value = !accordionInterno.value
  emit('update:accordionAberto', accordionInterno.value)
}

defineExpose({ carregarDocumentos, abrirModalDocumento })
</script>

<template>
  <div class="card mb-3 border-info-subtle">
    <div
      class="card-header bg-info-subtle py-2 d-flex justify-content-between align-items-center"
      :class="{ 'cursor-pointer': props.comAccordion }"
      @click="toggleAccordion"
    >
      <h5 class="mb-0 text-info-emphasis fw-bold">
        <i class="bi bi-file-earmark-text me-2"></i>Documentos de Entrada
        <span
          v-if="documentosEntrada.length > 0"
          class="ms-3 fw-normal fs-6 badge bg-info bg-opacity-10 text-info-emphasis border border-info-subtle"
        >
          {{ documentosEntrada.length }}
          {{ documentosEntrada.length === 1 ? 'documento' : 'documentos' }}
        </span>
        <span
          v-if="props.lstStatusOrdemCompra && !statusOcPermiteDocumento"
          class="ms-2 badge bg-warning bg-opacity-10 text-warning-emphasis border border-warning-subtle"
          title="Status da Ordem de Compra não permite vincular Documentos de Entrada"
        >
          <i class="bi bi-exclamation-triangle me-1"></i>
          OC {{ statusOcNome }}
        </span>
      </h5>
      <div class="d-flex align-items-center gap-2">
        <button
          v-if="props.podeEditar && statusOcPermiteDocumento"
          type="button"
          class="btn btn-sm btn-info text-white"
          @click.stop="handleNovoDocumento"
        >
          <i class="bi bi-plus-lg"></i> Novo Documento
        </button>
        <i
          v-if="props.comAccordion"
          class="bi fs-5"
          :class="accordionInterno ? 'bi-chevron-up' : 'bi-chevron-down'"
        ></i>
      </div>
    </div>
    <div class="card-body p-0" v-show="accordionInterno">
      <div class="grid-scroll-container">
        <!-- Loading -->
        <div v-if="isLoading" class="text-center py-4">
          <div class="spinner-border spinner-border-sm text-info me-2"></div>
          Carregando documentos...
        </div>

        <!-- Grid -->
        <table v-else class="table table-hover admin-table mb-0">
          <thead>
            <tr>
              <th style="width: 60px;">ID</th>
              <th>Nº Doc.</th>
              <th>Tipo</th>
              <th v-if="props.exibirPrimeiroProduto">Produto</th>
              <th v-if="props.exibirPrimeiroProduto">Fornecedor</th>
              <th>Operação</th>
              <th>Emissão</th>
              <th class="text-end">Valor Total</th>
              <th>Status</th>
              <th style="width: 120px;">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="doc in documentosEntrada"
              :key="doc.idDocumentoEntrada"
              @click="selecionarDocumento(doc)"
              class="cursor-pointer"
              :class="{
                'linha-selecionada border-start border-4 border-info': props.documentoSelecionado?.idDocumentoEntrada === doc.idDocumentoEntrada,
                'text-muted opacity-50': doc.lstStatusDocumento === SUP_ST_DOC_ENT.Cancelado
              }"
              :title="doc.lstStatusDocumento === SUP_ST_DOC_ENT.Cancelado ? 'Documento cancelado não pode ser selecionado' : 'Selecionar documento'"
            >
              <td><code>#{{ doc.idDocumentoEntrada }}</code></td>
              <td><code>{{ doc.numeroDocumento }}</code></td>
              <td>{{ doc.tipoDocumentoEntradaNome || '-' }}</td>
              <td v-if="props.exibirPrimeiroProduto">{{ doc.primeiroProdutoSku || doc.primeiroProdutoNome }}{{ doc.primeiroProdutoNome ? ' (' + doc.primeiroProdutoNome + ')' : '' }}</td>
              <td v-if="props.exibirPrimeiroProduto">{{ doc.parceiroFornecedorApelido || '-' }}</td>
              <td>{{ doc.operacaoDescricao || '-' }}</td>
              <td>{{ doc.dataEmissao ? new Date(doc.dataEmissao).toLocaleDateString() : '-' }}</td>
              <td class="text-end fw-bold">{{ formatarDecimal(doc.valorTotalDocumento) }}</td>
              <td>
                <span :class="['badge', obterCorStatus(doc.lstStatusDocumento)]">
                  {{ getStatusTexto(doc.lstStatusDocumento) }}
                </span>
              </td>
              <td>
                <DocumentoEntradaAcoesGrid
                  :documento="doc"
                  :tem-modulo-documento="props.temModuloDocumento"
                  :tem-completo="props.temCompleto"
                  :tem-visualizacao="props.temVisualizacao"
                  :pode-editar="props.podeEditar"
                  @editar="handleEditar"
                  @visualizar="handleVisualizar"
                  @aprovar="handleAprovar"
                  @acao-completa="handleAcaoCompleta"
                  @show-notification="(p) => emit('show-notification', p)"
                />
              </td>
            </tr>
            <tr v-if="documentosEntrada.length === 0">
              <td :colspan="props.exibirPrimeiroProduto ? 10 : 8" class="text-center py-4 text-muted">
                <i class="bi bi-info-circle me-2"></i>
                Nenhum documento de entrada cadastrado.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- MODAL: Cadastro/Edição/Visualização de Documento de Entrada -->
    <DocumentoEntradaModal
      v-model="showModalDocumento"
      :modo="modalDocumentoModo"
      :documento="modalDocumentoData"
      :id-filial="props.idFilial"
      :lst-moeda="props.lstMoeda"
      :id-operacao="props.idOperacao"
      :id-parceiro-negocio-emissor="props.idParceiroNegocioEmissor"
      :is-interno="props.isInterno"
      :id-purchase-order="props.idPurchaseOrder"
      :id-embarque="props.idEmbarque"
      :id-embarque-container="props.idEmbarqueContainer"
      :id-ordem-compra="props.idOrdemCompra"
      :id-organizacao="props.idOrganizacao || authStore.organizacaoAtiva"
      :listas="props.listas"
      :loading="false"
      :lst-tipo-taxa-conversao="props.lstTipoTaxaConversao"
      :com-smart-mode="props.comSmartMode"
      :habilitar-parceiro-produtor="props.habilitarParceiroProdutor"
      :config-busca-lote-fabricacao="props.configBuscaLoteFabricacao"
      :id-parceiro-negocio-fornecedor="props.idParceiroNegocioFornecedor"
      :contexto-origem="props.contextoOrigem"
      :modo-alocacao="props.modoAlocacao"
      :lst-tipos-local-estoque="props.lstTiposLocalEstoque"
      :id-local-estoque-padrao="props.idLocalEstoquePadrao"
      :id-condicao-pagamento="props.idCondicaoPagamento"
      :lst-forma-pagamento="props.lstFormaPagamento"
      :lst-forma-pagamento-internacional="props.lstFormaPagamentoInternacional"
      :lst-status-ordem-compra="props.lstStatusOrdemCompra"
      :lst-local-documento="lstLocalDocumento"
      :approval-config="approvalConfigReativo"
      :on-salvar-documento="handleSalvarDocumentoAprovacao"
      :on-cancelar-aprovacao="handleCancelarAprovacao"
      @salvar="handleSalvarDocumentoModal"
      @salvar-intermediario="handleSalvarDocumentoIntermediario"
      @show-notification="(p) => emit('show-notification', p)"
      @cancelar-aprovacao="handleCancelarAprovacao"
    />
  </div>
</template>

<style scoped>
.grid-scroll-container {
  overflow-x: auto;
}

.linha-selecionada {
  background-color: var(--bs-info-bg-subtle);
}
</style>
